import { useEffect, useState } from 'react';

interface DeployRun {
  buildNumber: string | null;
  result: string | null;
  status: string | null;
  finishedAt: string | null;
  sourceBranch: string | null;
  error?: string;
}

interface AdoSnapshot {
  schemaVersion: number;
  generatedAt: string;
  project: string;
  org: string;
  areaPath: string;
  workItems: {
    total: number;
    byType: Record<string, number>;
    byState: Record<string, number>;
    byPriority: Record<string, number>;
    currentSprint: { name: string; openCount: number } | null;
  };
  topPriority: Array<{ id: number; title: string; type: string; state: string; priority: number }>;
  lastDeploys: Record<string, DeployRun | null>;
}

const SNAPSHOT_URL = 'data/ado-snapshot.json';

function relativeTime(iso: string | null): string {
  if (!iso) return '—';
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return '—';
  const ms = Date.now() - t;
  const m = Math.round(ms / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  return `${d}d ago`;
}

export default function ProjectStatusTile() {
  const [snap, setSnap] = useState<AdoSnapshot | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetch(SNAPSHOT_URL, { cache: 'no-cache' })
      .then(async (res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = (await res.json()) as AdoSnapshot;
        if (!cancelled) setSnap(json);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="tile-card project-status-tile">
      <div className="tile-header">
        <h3 className="tile-title">Project status</h3>
      </div>
      <p className="tile-help">
        Live snapshot of the Synergy Platform ADO project — open work items, top
        priorities, and last deploy per environment. Refreshed hourly.
      </p>

      <div className="tile-body">
        {loading && <p className="tile-loading">Loading snapshot…</p>}

        {!loading && error && (
          <p className="tile-empty">
            Snapshot not available yet. The hourly pipeline writes{' '}
            <code>dashboard/public/data/ado-snapshot.json</code>; once it has run, this tile
            will populate.
          </p>
        )}

        {!loading && snap && (
          <>
            <dl className="stats-grid">
              <div>
                <dt>Open work items</dt>
                <dd>{snap.workItems.total}</dd>
              </div>
              <div>
                <dt>Current sprint</dt>
                <dd>{snap.workItems.currentSprint?.openCount ?? '—'}</dd>
              </div>
              <div>
                <dt>P1 / P2</dt>
                <dd>
                  {(snap.workItems.byPriority['1'] ?? 0) +
                    (snap.workItems.byPriority['2'] ?? 0)}
                </dd>
              </div>
              <div>
                <dt>Bugs</dt>
                <dd>{snap.workItems.byType['Bug'] ?? 0}</dd>
              </div>
            </dl>

            {snap.topPriority.length > 0 && (
              <>
                <h4 className="ai-detail-title">Top priority</h4>
                <ul className="ai-list">
                  {snap.topPriority.slice(0, 3).map((wi) => (
                    <li key={wi.id}>
                      <a
                        href={`${snap.org}/${encodeURIComponent(snap.project)}/_workitems/edit/${wi.id}`}
                        target="_blank"
                        rel="noreferrer noopener"
                      >
                        #{wi.id} — {wi.title}
                      </a>
                      <div className="ai-msg">
                        {wi.type} · {wi.state} · P{wi.priority}
                      </div>
                    </li>
                  ))}
                </ul>
              </>
            )}

            <h4 className="ai-detail-title">Last deploy by env</h4>
            <ul className="ai-list">
              {(['test', 'dev', 'uat', 'prod'] as const).map((env) => {
                const run = snap.lastDeploys?.[env];
                return (
                  <li key={env}>
                    <strong>{env.toUpperCase()}</strong>
                    {run ? (
                      <>
                        {' '}
                        — {run.result ?? run.status ?? 'unknown'}{' '}
                        <span className="muted">{relativeTime(run.finishedAt)}</span>
                      </>
                    ) : (
                      <span className="muted"> — never</span>
                    )}
                  </li>
                );
              })}
            </ul>

            <div className="tile-footer">
              <span className="muted">Snapshot at {relativeTime(snap.generatedAt)}</span>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
